import { useReducer, useEffect, useCallback } from 'react';
import { getUser, storeUser } from '@services/storage';
import { postRefreshToken, authMe } from '../services/auth';
import { ACTIONS } from './auth.actions';
import { InitialAuth, AuthContext } from './auth.context';
import { AuthActions, AuthState } from './auth.types';

const AuthReducer = (
  prevState = InitialAuth,
  action: AuthActions,
): AuthState => {
  switch (action.type) {
    case ACTIONS.LOGIN: {
      return {
        ...prevState,
        isAuthenticated: true,
        isLoading: false,
        user: action.user,
      };
    }
    case ACTIONS.LOGOUT:
      return { ...InitialAuth, isLoading: false };
    case ACTIONS.LOADING:
      return { ...prevState, isLoading: false };
    case ACTIONS.PERMISSIONS:
      return {
        ...prevState,
        permissions: action.permissions,
        area_leader: action.area_leader,
      };
    default:
      return InitialAuth;
  }
};

export const AuthProvider = (props: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(AuthReducer, InitialAuth);

  async function refreshToken() {
    const user = await getUser();
    if (user != null) {
      const data = await postRefreshToken({ refresh: user.refresh });
      storeUser({ ...user, refresh: data.refresh, access: data.access });
    }
  }

  const getMe = useCallback(async () => {
    const data = await authMe();
    dispatch({
      type: ACTIONS.PERMISSIONS,
      permissions: data.permissions,
      area_leader: data.area_leader,
    });
  }, []);

  useEffect(() => {
    async function checkStorage() {
      const user = await getUser();
      if (user) {
        dispatch({ type: ACTIONS.LOGIN, user: user });
      } else {
        dispatch({ type: ACTIONS.LOADING });
      }
    }
    checkStorage();
    if (state.isAuthenticated) {
      const tokenRefreshInterval = 300000; // 5 minutes
      const timerId = setInterval(refreshToken, tokenRefreshInterval);
      getMe();
      return () => {
        clearInterval(timerId);
      };
    }
  }, [state.isAuthenticated, getMe]);

  return (
    <AuthContext.Provider value={{ ...state, dispatch }}>
      {props.children}
    </AuthContext.Provider>
  );
};
